import { PO_LINE_QTY_MAX, PO_LINE_QTY_MAX_DIGITS } from '../NewPurchaseOrder/createConstants';

/**
 * Create-PO helpers shared by chatbot PDF review.
 * Ported from quickvee-ai-react `src/utils/po*.js` — keep formulas identical.
 */

export const PO_QTY_MIN_MESSAGE = 'Quantity must be at least 1.';
export const DUPLICATE_UPC_MESSAGE =
  'Duplicate UPC detected. Each UPC must be unique and cannot be added more than once.';
export const PO_STOCK_DUE_BEFORE_ISSUE_MSG =
  'Stock due date cannot be earlier than the issued date.';

/** PO cost fields only — never retail `price` / `unit_price` for Cost Per Item. */
export const CREATE_PO_COST_KEYS = [
  'costperItem',
  'cost_per_item',
  'costPerItem',
  'cost',
  'unit_cost',
  'purchase_price',
  'wholesale_price',
];

export function toPoIntQty(raw) {
  if (raw === null || raw === undefined || raw === '') return 0;
  const value = Number(raw);
  if (!Number.isFinite(value)) return 0;
  return Math.trunc(value);
}

/** Create PO After column: on-hand + order qty. */
export function poCreateAfterQtyDisplay(currentQty, requiredQty) {
  return toPoIntQty(currentQty) + toPoIntQty(requiredQty);
}

export function normalizeProductUpc(value) {
  return String(value ?? '').replace(/\s/g, '');
}

export function looksLikeUpc(query) {
  const digits = normalizeProductUpc(query);
  return digits.length >= 6 && /^\d+$/.test(digits);
}

export function isStockDueBeforeIssue(stockIso, issueIso) {
  if (!stockIso || !issueIso) return false;
  return String(stockIso).trim() < String(issueIso).trim();
}

/** Same digit cap as Create PO (`PO_LINE_QTY_MAX_DIGITS` = 6 → max 999999). */
export function tryPurchaseOrderQtyInput(raw) {
  const digits = String(raw ?? '')
    .replace(/[^\d]/g, '')
    .slice(0, PO_LINE_QTY_MAX_DIGITS);
  if (digits === '') return { ok: true, value: '' };
  const number = Number(digits);
  if (!Number.isFinite(number) || number <= 0) {
    return { ok: false, message: PO_QTY_MIN_MESSAGE };
  }
  return {
    ok: true,
    value: String(Math.min(PO_LINE_QTY_MAX, Math.floor(number))),
  };
}

export function readCreatePoCostPerItemNumber(obj) {
  if (!obj || typeof obj !== 'object') return null;
  for (const key of CREATE_PO_COST_KEYS) {
    const value = obj[key];
    if (value != null && value !== '') {
      const number = Number(value);
      if (Number.isFinite(number) && number >= 0) return number;
    }
  }
  return null;
}

export function formatCreatePoCostPerItem(number) {
  if (number == null || !Number.isFinite(number) || number < 0) return '0.00';
  return number.toFixed(2);
}

/** Invoice extract may only have unit_price — use that for prefill, not catalog retail. */
export function costPerItemFromExtractedLine(extracted, line) {
  const fromCostKeys = readCreatePoCostPerItemNumber(extracted) ??
    readCreatePoCostPerItemNumber(line);
  if (fromCostKeys != null) return formatCreatePoCostPerItem(fromCostKeys);
  const unitPrice = Number(extracted?.unit_price ?? line?.unit_price);
  if (Number.isFinite(unitPrice) && unitPrice >= 0) {
    return formatCreatePoCostPerItem(unitPrice);
  }
  return '';
}

export function findDuplicateUpcRows(lines) {
  const seen = new Map();
  const dupRows = new Set();
  (lines || []).forEach((line, index) => {
    const upc = normalizeProductUpc(line?.upc);
    if (!upc) return;
    if (seen.has(upc)) {
      dupRows.add(index);
      dupRows.add(seen.get(upc));
    } else {
      seen.set(upc, index);
    }
  });
  return dupRows;
}

export function poLineProductVariantSelectionKey(line) {
  const productId = String(line?.productId || '');
  if (!productId) return '';
  const upc = normalizeProductUpc(line?.upc);
  if (upc) return `${productId}|u:${upc}`;
  const variantId = String(line?.variantId || '').trim();
  if (variantId && variantId !== '0') return `${productId}|v:${variantId}`;
  return `${productId}|base`;
}

const PRODUCT_ON_HAND_KEYS = [
  'quantity',
  'qty',
  'current_qty',
  'stock_qty',
  'inventory_qty',
  'qty_on_hand',
  'available_qty',
  'available_quantity',
  'remaining_qty',
  'total_qty',
  'item_qty',
];

export function productOnHandQtyFromApi(product) {
  if (!product || typeof product !== 'object') return 0;
  for (const key of PRODUCT_ON_HAND_KEYS) {
    const value = product[key];
    if (value != null && value !== '') return toPoIntQty(value);
  }
  return 0;
}

function getProductByIdPayload(raw) {
  if (!raw || typeof raw !== 'object') return { pd: null, variants: [] };
  const data =
    (raw.data && typeof raw.data === 'object' ? raw.data : null) ||
    (raw.result && typeof raw.result === 'object' ? raw.result : null);
  const pd =
    data?.productdata && typeof data.productdata === 'object'
      ? data.productdata
      : raw.productdata && typeof raw.productdata === 'object'
        ? raw.productdata
        : null;
  const variants = Array.isArray(data?.product_variants)
    ? data.product_variants
    : Array.isArray(data?.product_varients)
      ? data.product_varients
      : [];
  return { pd, variants };
}

export function findProductVariantByUpc(variants, upc) {
  if (!Array.isArray(variants) || !upc) return null;
  const target = normalizeProductUpc(upc);
  for (const variant of variants) {
    if (!variant || typeof variant !== 'object') continue;
    for (const key of ['upc', 'upc_code', 'barcode', 'variant_upc']) {
      if (normalizeProductUpc(variant[key]) === target) return variant;
    }
  }
  return null;
}

/**
 * Map get_productdata_ById (+ list row) → Create PO line stock/cost/upc/variant.
 * Same contract as AI `resolveCreatePoLineFromProductById`.
 */
export function resolveCreatePoLineFromProductById(raw, productRow) {
  const { pd, variants } = getProductByIdPayload(raw);
  const listRaw = productRow?.raw ?? productRow ?? {};
  const preferredUpc = normalizeProductUpc(
    listRaw.upc ?? productRow?.upc ?? pd?.upc ?? '',
  );
  const variantPinnedByUpc = preferredUpc
    ? findProductVariantByUpc(variants, preferredUpc)
    : null;

  let matchedVariant = null;
  if (variantPinnedByUpc) matchedVariant = variantPinnedByUpc;
  else if (variants.length === 1) matchedVariant = variants[0];

  let currentQty = 0;
  if (matchedVariant) currentQty = productOnHandQtyFromApi(matchedVariant);
  if (!variantPinnedByUpc && currentQty === 0 && pd) {
    const parentQty = productOnHandQtyFromApi(pd);
    if (parentQty > 0) currentQty = parentQty;
  }
  if (currentQty === 0 && !variantPinnedByUpc) {
    currentQty = productOnHandQtyFromApi(productRow);
  }

  const costFromVariant = readCreatePoCostPerItemNumber(matchedVariant);
  const costFromParent = readCreatePoCostPerItemNumber(pd);
  const costFromList = readCreatePoCostPerItemNumber(listRaw);
  const costNum = costFromVariant ?? costFromParent ?? costFromList;

  const upcFromVariant = matchedVariant
    ? String(
        matchedVariant.upc ??
          matchedVariant.upc_code ??
          matchedVariant.barcode ??
          '',
      ).trim()
    : '';
  const upcOut =
    upcFromVariant ||
    (pd && String(pd.upc ?? '').trim()) ||
    String(listRaw.upc ?? productRow?.upc ?? '').trim();

  const variantId =
    matchedVariant?.variant_id ??
    matchedVariant?.varient_id ??
    matchedVariant?.id ??
    matchedVariant?.variantId;

  return {
    stock: currentQty,
    cost: formatCreatePoCostPerItem(costNum),
    upc: upcOut,
    variantId:
      variantId != null && String(variantId).trim() !== ''
        ? String(variantId)
        : '0',
  };
}

export function lineTotalAmount(qty, cost) {
  const quantity = Number(qty);
  const unitCost = Number(cost);
  if (!Number.isFinite(quantity) || !Number.isFinite(unitCost)) return 0;
  return quantity * unitCost;
}
